import React, { Suspense, lazy, useEffect } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { OperateWorkspaceProvider } from '../operate/OperateWorkspaceContext';
import EngineeringContextPicker from '../operate/EngineeringContextPicker';
import { OperateDomainSecondaryNav } from './OperateSubNav';
import MissionProgressHeader from './MissionProgressHeader';
import { ErrorBoundary } from '../../ErrorBoundary';
import SectionOutlet from './SectionOutlet';
import { operateTabLoader, routeLoaders } from '../../../routes/routeLoaders';

// The Engineering tab's timeline card, shown in hero-only mode on Testing and Executions.
const EngineeringHero = lazy(routeLoaders.operateFixes);

/** Operate shell — horizontal padding from workspace-chrome-shell. */
const OperateLayout: React.FC = () => {
  const { pathname } = useLocation();
  // Start the destination tab chunk while this (eager) shell mounts so the
  // download overlaps chrome render instead of waiting layout-then-page.
  useEffect(() => {
    void operateTabLoader(pathname)?.().catch(() => undefined);
  }, [pathname]);
  const hidePicker =
    pathname.includes('/operate/incident') ||
    pathname.includes('/operate/incidents') ||
    pathname.includes('/operate/runtime-logs') ||
    pathname.includes('/operate/reported') ||
    pathname.includes('/operate/requests') ||
    pathname.includes('/operate/missions') ||
    pathname.includes('/operate/replay') ||
    /\/operate\/?$/.test(pathname.replace(/\?.*$/, ''));
  const hideMissionProgress =
    // Engineering shows its own hero (pipeline + blocker) under the Mission selector.
    pathname.includes('/operate/engineering') ||
    pathname.includes('/operate/fixes') ||
    pathname.includes('/operate/executions') ||
    pathname.includes('/operate/test-environment') ||
    pathname.includes('/operate/requests') ||
    pathname.includes('/operate/incidents') ||
    // Runtime logs is file-level triage; one incident's mission would read as the page's status.
    pathname.includes('/operate/runtime-logs') ||
    pathname.includes('/operate/reported');
  const showEngineeringHero =
    pathname.includes('/operate/test-environment') || pathname.includes('/operate/executions');

  return (
    <OperateWorkspaceProvider>
      <Box
        className="operate-workspace-page operate-overview-shell mirae-scrollbar"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          pt: { xs: 0.5, md: 1 },
          pb: { xs: 2, md: 3 },
          gap: 1.5,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <OperateDomainSecondaryNav />
        {/* Page-level section tabs (e.g. Engineering review) portal in here. */}
        <Box id="operate-page-tabs" sx={{ '&:empty': { display: 'none' } }} />
        {!hidePicker && <EngineeringContextPicker chromeInCard={!hideMissionProgress} />}
        {/* Engineering timeline card portals in here, directly under the Mission selector. */}
        <Box id="operate-page-timeline" sx={{ '&:empty': { display: 'none' } }} />
        {showEngineeringHero && (
          <ErrorBoundary region="engineering-hero" resetKey={pathname}>
            <Suspense fallback={null}>
              <EngineeringHero heroOnly />
            </Suspense>
          </ErrorBoundary>
        )}
        {!hideMissionProgress && (
          <ErrorBoundary region="mission-progress">
            <MissionProgressHeader />
          </ErrorBoundary>
        )}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Keyed on the path: this layout stays mounted across Operate tabs, so
              without a reset one tab's error would stick to every other tab. */}
          <ErrorBoundary region="operate-outlet" resetKey={pathname}>
            <SectionOutlet section="operate" />
          </ErrorBoundary>
        </Box>
      </Box>
    </OperateWorkspaceProvider>
  );
};

export default OperateLayout;
